import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { StyledPageTitle } from 'theme/styles';
import TableFormWrapper from 'components/atoms/FormWrapper';
import Grid from '@mui/material/Grid';
import { StyledValueFormHeader } from 'pages/SystemSettingsPage/styles';
import { Box } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { useNavigate } from 'react-router-dom';
import { Resolver, useForm } from 'react-hook-form';
import { useScroll } from 'hooks/useScroll';
import { ActionTypes } from 'state/actions/alert';
import { Schema } from 'pages/SystemSettingsPage/UnitsCurrencySetupPage/schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUnitsCurrencySetup } from 'pages/SystemSettingsPage/UnitsCurrencySetupPage/hooks';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';

export interface IUnitsCurrencyFormValues {
  currency: string;
  measurement: string;
}

export const UnitsCurrencySetupPage = () => {
  const { t } = useTranslations();
  const { samePageAlert, clearAlert, setAlert } = useAlert();
  const navigate = useNavigate();
  const { selectedLanguage } = useLang();
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<IUnitsCurrencyFormValues>({
    currency: '',
    measurement: '',
  });
  const {
    updateUnitsCurrencyData,
    loadInitData,
    currencyValue,
    measurementValue,
    loadDictionaries,
    currency,
    measurement,
  } = useUnitsCurrencySetup();
  const schema = Schema();
  const { elRef, scrollToError } = useScroll();

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.measurementUnit]);
      await loadDictionaries([EDictionaryTypes.currency]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IUnitsCurrencyFormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(schema) as unknown as Resolver<IUnitsCurrencyFormValues>,
  });

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    loadInitData();
  }, []);

  useEffect(() => {
    if (currencyValue) {
      setValue('currency', currencyValue);
      setInitialValues((prevState) => ({
        ...prevState,
        currencyValue,
      }));
    }
  }, [currencyValue]);

  useEffect(() => {
    if (measurementValue) {
      setValue('measurement', measurementValue);
      setInitialValues((prevState) => ({
        ...prevState,
        measurementValue,
      }));
    }
  }, [measurementValue]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const formChanged = (
    formInitialValues?: IUnitsCurrencyFormValues | null,
    formCurrentValues?: IUnitsCurrencyFormValues | null,
  ) =>
    formInitialValues?.measurement !== formCurrentValues?.measurement ||
    formInitialValues?.currency !== formCurrentValues?.currency;

  const onSubmitForm = async (data: IUnitsCurrencyFormValues) => {
    if (!formChanged(initialValues, data)) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('general.noChanged.warning.message'),
        variant: EAlertVariants.warning,
      });
      return;
    }
    setSubmitLoading(true);
    await updateUnitsCurrencyData(data);
    setSubmitLoading(false);
    setInitialValues(data);
    setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
      text: t('unitsCurrencySetup.success.text'),
    });
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          t('unitsCurrencySetup.header'),
        ]}
      />
      <StyledPageTitle variant="h4">{t('unitsCurrencySetup.header')}</StyledPageTitle>
      <TableFormWrapper>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Grid container ref={elRef}>
            <Grid container size={{ xs: 12 }} sx={{ rowGap: 1 }}>
              <Grid container size={{ xs: 12 }} spacing={1}>
                <Grid size={{ xs: 12 }}>
                  <StyledValueFormHeader>
                    {t('unitsCurrencySetup.units.header')}
                  </StyledValueFormHeader>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormSelect
                    options={mapDictionaryToOptionProp(measurement)}
                    name="measurement"
                    label={t('unitsCurrencySetup.measurement.label')}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                  />
                </Grid>
              </Grid>
              <Grid container size={{ xs: 12 }} spacing={1}>
                <Grid size={{ xs: 12 }}>
                  <StyledValueFormHeader>
                    {t('unitsCurrencySetup.currency.header')}
                  </StyledValueFormHeader>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormSelect
                    options={mapDictionaryToOptionProp(currency)}
                    name="currency"
                    label={t('unitsCurrencySetup.currency.label')}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 1, mt: 1 }}>
              <Grid size={{ xs: 12, lg: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  label={t('general.save.button')}
                  isLoading={isSubmitLoading}
                  id="save-button"
                  variant={EButtonVariants.contained}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 3 }}>
                <Button
                  fullWidth
                  label={t('general.cancel.button')}
                  id="cancel-button"
                  variant={EButtonVariants.outlined}
                  onClick={() => navigate(ERouteLinks.Administration)}
                />
              </Grid>
            </Grid>
          </Grid>
        </form>
      </TableFormWrapper>
      {samePageAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
    </>
  );
};
